import { Link, useNavigate } from "react-router-dom";
import {
  Container, Row, Card, Button, Breadcrumb, Col
} from "react-bootstrap";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page as Paging,
  PageSettingsModel,
  Filter,
  Resize,
  FilterSettingsModel
} from "@syncfusion/ej2-react-grids";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider
} from "@whitespace/whitespace-ui";
import { useEffect, useState, useCallback } from "react";
import { useStateMachine } from "little-state-machine";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IRegion from "interfaces/IRegion";
import { parseUsingParseInt, stringIsEmpty } from "helpers/stringUtils";
import { RolesEnum } from "modules/rolesEnum";
import "styles/sf-datagrid.scss";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import RegionStatusTemplate from "./Templates/RegionStatusTemplate";
import EditRegionTemplate from "./Templates/EditRegionTemplate";

const pageSize: number = parseUsingParseInt(process.env.REACT_APP_PAGE_SIZE);
const pageSettings: PageSettingsModel = {
  pageSize,
  pageSizes: [5, 10, 20, 50, "All"]
};
const filterSettings: FilterSettingsModel = { type: "Excel" };
let grid: GridComponent | undefined;

const activeTemplateInstance = RegionStatusTemplate;

const editTemplateInstance = EditRegionTemplate;

function IsButtonVisible(role: string | undefined): boolean {
  return !stringIsEmpty(role) && role === RolesEnum.SuperAdmin;
}

const ManageRegions = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const { state } = useStateMachine();
  const navigate = useNavigate();
  const [renderingComplete, setRenderingComplete] = useState(false);

  const {
    isSending, errors, data, statusCode, sendRequest
  } = useAmplifyREST();
  const [regions, setRegions] = useState<IRegion[] | undefined>(undefined);

  useEffect(() => {
    sendRequest("settings/regions", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data && data.regions) setRegions(data.regions);
  }, [data, setRegions]);

  useEffect(() => {
    if (![0, 200].includes(statusCode)) {
      setRenderingComplete(true);
    }
  }, [statusCode]);

  const handleNavToSettings = () => {
    navigate(RoutePathsEnum.Settings);
  };

  useEffect(() => {
    if (errors) setShowExceptions(true);
  }, [errors]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="region-management">
              Region Management
              <Spinner
                animation="grow"
                visible={isSending || !renderingComplete}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
          {IsButtonVisible(state.userRole) && (
            <div className="float-end">
              <IconButton
                data-testid="add-region"
                iconType={IconButtonType.Add}
                variant="outline-primary btn-create"
                onClick={() => navigate(RoutePathsEnum.SettingsRegionsAdd)}
              >
                Add New Region
              </IconButton>
            </div>
          )}
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={8} className="d-flex">
              <Breadcrumb className="left-margin-large no-wrap">
                <Breadcrumb.Item
                  data-testid="breadcrumb-settings"
                  linkAs={Link}
                  linkProps={{ to: RoutePathsEnum.Home }}
                  onClick={handleNavToSettings}
                >
                  Settings
                </Breadcrumb.Item>
                <Breadcrumb.Item active data-testid="breadcrumb-regions">
                  Regions
                </Breadcrumb.Item>
              </Breadcrumb>
            </Col>
          </Row>
          <Divider />
          <section className="regions pt-2">
            <Container fluid>
              <Row>
                <div className="control-pane">
                  <div className="control-section">
                    <GridComponent
                      ref={(g: GridComponent) => {
                        grid = g;
                      }}
                      id="overviewgrid"
                      data-testid="regions"
                      allowPaging
                      allowResizing
                      pageSettings={pageSettings}
                      allowSorting
                      allowFiltering
                      filterSettings={filterSettings}
                      allowSelection={false}
                      dataSource={regions}
                      actionComplete={() => {
                        makeGridAACompliant(grid!, () => {
                          setRenderingComplete(true);
                        });
                      }}
                      loadingIndicator={{ indicatorType: "Shimmer" }}
                      type=""
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="id"
                          headerText="id"
                          visible={false}
                          isPrimaryKey
                        />
                        <ColumnDirective
                          field="name"
                          headerText="Region Name"
                        />
                        <ColumnDirective
                          field="active"
                          headerText="Status"
                          template={activeTemplateInstance}
                        />
                        {IsButtonVisible(state.userRole) && (
                          <ColumnDirective
                            headerText=""
                            visible={IsButtonVisible(state.userRole)}
                            allowSorting={false}
                            allowResizing={false}
                            width={20}
                            template={editTemplateInstance}
                            allowFiltering={false}
                          />
                        )}
                      </ColumnsDirective>
                      <Inject services={[Filter, Paging, Sort, Resize]} />
                    </GridComponent>
                  </div>
                </div>
              </Row>
            </Container>
          </section>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default ManageRegions;
